import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Loader2, MailPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAccountAuth } from '@/app/hooks/useAccountAuth';
import { accountOrganizationApi } from '@/services/endpoints/account/organization';
import { ACCOUNT_ORG_KEYS } from '@/components/account/queryKeys';
import {
  accountInputClass,
  EmptyState,
  FieldLabel,
  LoadingRows,
  Notice,
  Panel,
  SectionTitle,
  StatusPill,
  formatDay,
} from '@/components/account/accountUi';
import type { OrganizationMember, OrganizationProfileUpdate } from '@/services/api/accountTypes';

function apiMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  if (axiosError?.response?.status === 403) {
    return 'Your role does not allow this change. Ask the organization owner.';
  }
  return axiosError?.response?.data?.message || axiosError?.response?.data?.error || fallback;
}

function memberName(member: OrganizationMember): string {
  const name = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
  return name || member.workEmail || member.email || 'Unnamed member';
}

function roleName(member: OrganizationMember): string {
  if (!member.role) return '—';
  return typeof member.role === 'string' ? member.role : member.role.name;
}

const EDITABLE_FIELDS: Array<{
  key: keyof OrganizationProfileUpdate;
  label: string;
  placeholder?: string;
  type?: string;
  help?: string;
}> = [
  { key: 'name', label: 'Organization name' },
  { key: 'website', label: 'Website', type: 'url', placeholder: 'https://example.com' },
  { key: 'industry', label: 'Industry' },
  { key: 'size', label: 'Company size', placeholder: '51-200' },
  { key: 'location', label: 'Location', placeholder: 'Toronto, Canada' },
  { key: 'country', label: 'Country code', placeholder: 'CA', help: 'Two-letter ISO code.' },
  { key: 'businessAddress', label: 'Business address' },
  { key: 'emailFromAddress', label: 'Sender email', type: 'email' },
  { key: 'emailReplyTo', label: 'Reply-to email', type: 'email' },
];

export default function AccountOrganization() {
  const queryClient = useQueryClient();
  const { isOwner } = useAccountAuth();
  const [draft, setDraft] = useState<OrganizationProfileUpdate>({});
  const [dirty, setDirty] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');

  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ACCOUNT_ORG_KEYS.profile,
    queryFn: accountOrganizationApi.getProfile,
    staleTime: 5 * 60_000,
  });

  const { data: memberData, isLoading: loadingMembers } = useQuery({
    queryKey: ACCOUNT_ORG_KEYS.membersList,
    queryFn: () => accountOrganizationApi.getMembers({ limit: 25 }),
    staleTime: 60_000,
  });

  const { data: invitations } = useQuery({
    queryKey: ACCOUNT_ORG_KEYS.invitations,
    queryFn: accountOrganizationApi.getInvitations,
    staleTime: 60_000,
  });

  const { data: roles } = useQuery({
    queryKey: ACCOUNT_ORG_KEYS.roles,
    queryFn: accountOrganizationApi.getRoles,
    enabled: isOwner,
    staleTime: 10 * 60_000,
    retry: false,
  });

  // Seed the form once; typing then owns it, so a background refetch cannot
  // overwrite what the user is in the middle of editing.
  useEffect(() => {
    if (!profile || dirty) return;
    setDraft({
      name: profile.name ?? '',
      website: profile.website ?? '',
      industry: profile.industry ?? '',
      size: profile.size ?? '',
      location: profile.location ?? '',
      country: profile.country ?? '',
      businessAddress: profile.businessAddress ?? '',
      emailFromAddress: profile.emailFromAddress ?? '',
      emailReplyTo: profile.emailReplyTo ?? '',
    });
  }, [profile, dirty]);

  const saveProfile = useMutation({
    mutationFn: (input: OrganizationProfileUpdate) => accountOrganizationApi.updateProfile(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_ORG_KEYS.profile });
      setDirty(false);
      toast.success('Organization updated.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not save the organization.')),
  });

  const invite = useMutation({
    mutationFn: () =>
      accountOrganizationApi.sendInvitation({ email: inviteEmail.trim(), roleId: inviteRoleId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_ORG_KEYS.invitations });
      setInviteEmail('');
      toast.success('Invitation sent.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not send the invitation.')),
  });

  const handleProfileSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Drop empties so a blank optional field is not submitted as "" — several
    // of these are format-validated server-side and would reject an empty
    // string. `country` must be the uppercase 2-letter ISO code.
    const payload: OrganizationProfileUpdate = {};
    EDITABLE_FIELDS.forEach(({ key }) => {
      const value = draft[key];
      if (typeof value !== 'string') return;
      const trimmed = value.trim();
      if (!trimmed) return;
      payload[key] = key === 'country' ? trimmed.toUpperCase() : trimmed;
    });
    saveProfile.mutate(payload);
  };

  const members = memberData?.members ?? [];
  const pendingInvitations = (invitations ?? []).filter(
    (invitation) => (invitation.status ?? 'pending').toLowerCase() === 'pending'
  );

  return (
    <>
      <SectionTitle
        title="Organization"
        description="Company details, the people in your workspace, and outstanding invitations."
      />

      <div className="space-y-6">
        <Panel title="Company details">
          {loadingProfile ? (
            <LoadingRows rows={5} />
          ) : (
            <form onSubmit={handleProfileSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                {EDITABLE_FIELDS.map((field) => (
                  <div key={field.key}>
                    <FieldLabel htmlFor={`org-${field.key}`}>{field.label}</FieldLabel>
                    <input
                      id={`org-${field.key}`}
                      type={field.type ?? 'text'}
                      className={accountInputClass}
                      placeholder={field.placeholder}
                      value={(draft[field.key] as string) ?? ''}
                      maxLength={field.key === 'country' ? 2 : undefined}
                      onChange={(event) => {
                        setDirty(true);
                        setDraft((current) => ({ ...current, [field.key]: event.target.value }));
                      }}
                    />
                    {field.help && (
                      <p className="mt-1.5 text-xs text-muted-foreground">{field.help}</p>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={saveProfile.isPending || !dirty}>
                  {saveProfile.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  Save changes
                </Button>
                {profile?.timezone && (
                  <p className="text-xs text-muted-foreground">
                    Timezone: <span className="font-mono">{profile.timezone}</span>
                  </p>
                )}
              </div>
            </form>
          )}
        </Panel>

        <Panel
          title="Members"
          description={
            memberData ? `${memberData.total} ${memberData.total === 1 ? 'person' : 'people'} in this organization.` : undefined
          }
          bodyClassName="p-0"
        >
          {loadingMembers ? (
            <div className="p-5">
              <LoadingRows rows={4} />
            </div>
          ) : members.length === 0 ? (
            <EmptyState icon={Users} title="No members yet" body="Invite someone below to get started." />
          ) : (
            <ul className="divide-y divide-border">
              {members.map((member) => (
                <li key={member.id} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{memberName(member)}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {member.workEmail ?? member.email ?? '—'}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs capitalize text-muted-foreground">
                    {roleName(member)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {memberData && memberData.total > members.length && (
            <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
              Showing {members.length} of {memberData.total}. Manage the full directory in the
              employee management app.
            </p>
          )}
        </Panel>

        <Panel
          title="Invitations"
          description="Invite someone to join this organization. They receive an email with a link to set up their account."
        >
          {isOwner ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                invite.mutate();
              }}
              className="flex flex-wrap items-end gap-3"
            >
              <div className="min-w-[16rem] flex-1">
                <FieldLabel htmlFor="invite-email">Email</FieldLabel>
                <input
                  id="invite-email"
                  type="email"
                  className={accountInputClass}
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="teammate@company.com"
                  required
                />
              </div>
              <div className="min-w-[12rem]">
                <FieldLabel htmlFor="invite-role">Role</FieldLabel>
                <select
                  id="invite-role"
                  className={accountInputClass}
                  value={inviteRoleId}
                  onChange={(event) => setInviteRoleId(event.target.value)}
                  required
                >
                  <option value="">Select a role</option>
                  {(roles ?? []).map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              <Button type="submit" disabled={invite.isPending || !inviteEmail || !inviteRoleId}>
                {invite.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <MailPlus className="h-4 w-4" aria-hidden="true" />
                )}
                Send invitation
              </Button>
            </form>
          ) : (
            <Notice tone="neutral" title="Owner only">
              Only the organization owner can invite new members.
            </Notice>
          )}

          {pendingInvitations.length > 0 && (
            <ul className="mt-5 divide-y divide-border border-t border-border">
              {pendingInvitations.map((invitation) => (
                <li key={invitation.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{invitation.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Sent {formatDay(invitation.createdAt)}
                      {invitation.expiresAt ? ` · expires ${formatDay(invitation.expiresAt)}` : ''}
                    </p>
                  </div>
                  <StatusPill tone="warn" className="px-2 py-0.5">
                    Pending
                  </StatusPill>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
